import './index.scss';

import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { BsPlusLg } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import axios from '../../admin-axios';
import useActions from '../../hooks/useActions';
import Invoice from '../../printer/Invoice';
import { detectError, nFormatter } from '../../utils';
import classes from '../ApprovedTransactionView/index.module.scss';
import CINBox from '../CINBox';
import { PromptModal, SuccessModal } from '../Modals';

const UnApprovedTransactionView = () => {
  const { txId } = useParams();
  const [payments, setPayments] = useState([
    {
      payment_method: '',
      amount: '',
      bank_name: ''
    }
  ]);
  const [customerId, setCustomerId] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [pricePerKg, setPricePerKg] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalKG, setTotalKG] = useState(0);
  const [canReviewPrice, setCanReviewPrice] = useState(false);
  const [reviewPrice, setReviewPrice] = useState(false);
  const [approvalPerson, setApprovalPerson] = useState('');
  const [prevTransactions, setPrevTransactions] = useState(null);
  const [approvalList, setApprovalList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [prevLoading, setPrevLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showApproveTransaction, setApproveTransactionShow] = useState(false);
  const [approveTransactionLoading, setApproveTransactionLoading] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(false);

  const handleApproveTransactionClose = () => setApproveTransactionShow(false);
  const handleApproveTransactionShow = () => setApproveTransactionShow(true);
  const { setSubHeader, setBackLink, getUnApprovedTransactions, getApprovedTransactions } =
    useActions();

  const {
    user: { stationId, id: user_id }
  } = useSelector((state) => state.auth);

  const openNav = () => {
    const element = document.getElementById('mySidenav');
    element.style.width = '280px';
    element.style.right = '0';
  };

  const closeNav = () => {
    const element = document.getElementById('mySidenav');
    element.style.width = '0';
    element.style.right = '-30px';
  };

  const prepareApprovalHandler = () => {
    let continueTransaction = true;
    //  check payment methods
    payments.forEach((payment) => {
      if (payment.payment_method === '') {
        toast.error('One or more payment method is empty');
        continueTransaction = false;
      }
      if (payment.amount === '') {
        toast.error('One or more amount input is empty');
        continueTransaction = false;
      }
      if (payment.payment_method === 'transfer' && payment.bank_name === '') {
        toast.error('Please fill in Bank Name for transfer payment method');
        continueTransaction = false;
      }
    });

    if (continueTransaction) {
      handleApproveTransactionShow();
    }
  };

  const approveTransactionHandler = async () => {
    try {
      if (transaction.transaction_id) {
        setApproveTransactionLoading(true);
        await axios.post(`/transaction/approve/${transaction.transaction_id}/${user_id}`, payments);
        setApproveTransactionLoading(false);
        handleApproveTransactionClose();
        setShowSuccess(true);
        getUnApprovedTransactions(stationId, 1);
        getApprovedTransactions(stationId, 1);
      }
    } catch (e) {
      setApproveTransactionLoading(false);
      toast.error(`Error Approving transaction: ${detectError(e)}`);
    }
  };

  const reviewPriceHandler = async () => {
    if (!reviewPrice) {
      toast.error('Please review price first');
      return;
    }

    if (!approvalPerson) {
      toast.error('Please select person for approval');
      return;
    }

    if (!pricePerKg || Number.isNaN(parseFloat(pricePerKg))) {
      toast.error('Please enter a new price');
      return;
    }

    if (Number.isNaN(parseFloat(pricePerKg))) {
      toast.error('Please enter a correct price');
      return;
    }

    if (transaction) {
      const toSend = {
        tranx_id: transaction.transaction_id,
        approval_id: approvalPerson,
        new_price: pricePerKg,
        user_id: user_id,
        total_gas_quantity: totalKG
      };

      try {
        setReviewLoading(true);
        await axios.post(`/transaction/review/price`, toSend);
        setReviewLoading(false);
        setReviewPrice(false);
        toast.success('Review Saved!');
      } catch (e) {
        setReviewLoading(false);
        toast.error('Cannot save review price');
      }
    }
  };

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`/transaction/unapproved/details/${txId}`);

        setSubHeader(response.data.customer_type);
        setBackLink('/unapproved');

        setTransaction(response.data);
        setPricePerKg(response.data.price);
        setTotalAmount(response.data.amount);
        setTotalKG(response.data.total_gas_quantity);
        setCanReviewPrice(response.data.customer_type === 'Retailer');
        setCustomerId(response.data.customer_id);
        setLoading(false);
      } catch (e) {
        setBackLink('/unapproved');
        setLoading(false);
      }
    };

    fetchTransaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txId]);

  useEffect(() => {
    const fetchApprovalList = async () => {
      try {
        setPrevLoading(true);

        const response = await axios.get(`/setting/approval/staff/${stationId}`);

        setApprovalList(response.data.data);
      } catch (e) {
        toast.error(`Error Loading Approval List ${e.message}`);
      }
    };

    fetchApprovalList();
  }, [stationId]);

  useEffect(() => {
    const fetchPrevTransaction = async () => {
      if (customerId) {
        try {
          setPrevLoading(true);

          const response = await axios.get(`/transaction/previous/${customerId}/${stationId}`);

          setPrevTransactions(response.data);
          setPrevLoading(false);
        } catch (e) {
          toast.error(`Error Loading previous transactions ${e.message}`);
          setPrevLoading(false);
        }
      }
    };

    fetchPrevTransaction();
  }, [customerId, stationId]);

  useEffect(() => {
    const fetchPrintData = async () => {
      if (transaction) {
        try {
          setLoadingPrint(true);

          const response = await axios.get(
            `/transaction/invoice/print/${transaction?.transaction_id}`
          );

          setPrintData(response.data);
          setLoadingPrint(false);
        } catch (e) {
          setLoadingPrint(false);
          toast.error('Invoice data could not be loaded!');
        }
      }
    };

    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction]);

  useEffect(() => {
    return () => {
      setBackLink('');
      setSubHeader('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <div>
        <div className="row g-5">
          {loading ? (
            <div className="d-flex justify-content-center w-100">
              <Spinner className="spinner" animation="border" variant="dark" />
            </div>
          ) : (
            <>
              <div className="col-xl-6">
                <div className={classes.view_transaction}>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-7">
                      <div className={`${classes.view_transaction_id} p-2`}>
                        <div className="d-flex align-items-center">
                          <span>Transaction ID</span>
                          <span>{transaction?.transaction_id}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-5">
                      <div className="h-100">
                        <button
                          onClick={() => window.print()}
                          type="button"
                          disabled={loadingPrint || printData === null}
                          className="btn print_transaction_button w-100 h-100">
                          Print Invoice
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className={`${classes.view_transaction_box} w-100 p-4 mb-4`}>
                    <h5>Cylinder Identification Numbers</h5>
                    <CINBox
                      cins={transaction ? transaction['Cylinder Identity Number'] : []}
                      total_quantity={transaction?.total_gas_quantity}
                    />
                  </div>
                  <div className="row mb-4">
                    <div className="col-6">
                      <div>
                        <div className="fw-bold mb-2">Price/Kg</div>
                        <div className={`${classes.view_transaction_pricekg} d-flex`}>
                          <span className="me-2">{transaction?.symbol}</span>{' '}
                          {/* {transaction?.price} */}
                          <span>
                            <input
                              type="text"
                              value={pricePerKg}
                              onChange={(event) => {
                                setPricePerKg(event.target.value);
                                try {
                                  setTotalAmount(
                                    (totalKG * parseFloat(event.target.value)).toFixed(2)
                                  );
                                } catch (e) {
                                  toast.error('Price per KG has to be a number');
                                }
                              }}
                              disabled={!reviewPrice}
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div>
                        <div className="fw-bold mb-2">Amount</div>
                        <div className={classes.view_transaction_amount}>
                          {transaction?.symbol} {totalAmount}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-sm-5">
                      <div>
                        <button
                          type="button"
                          disabled={!canReviewPrice}
                          onClick={() => setReviewPrice(true)}
                          className={`${classes.view_transaction_review} w-100 btn btn-outline-success`}>
                          Review Price
                        </button>
                      </div>
                    </div>
                    <div className="col-sm-7">
                      {/* <label className="fw-bold">Seek For Approval</label> */}
                      <select
                        value={approvalPerson}
                        disabled={!canReviewPrice}
                        onChange={(event) => {
                          setApprovalPerson(event.target.value);
                        }}
                        className="form-select">
                        <option value="">Seek For Approval</option>
                        {approvalList &&
                          approvalList.map((list) => {
                            return (
                              <option key={list.approval_id} value={list.approval_id}>
                                {list.firstname} {list.lastname}
                              </option>
                            );
                          })}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      {reviewLoading ? (
                        <div className="d-flex justify-content-center w-100">
                          <Spinner className="spinner" animation="border" variant="dark" />
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={!canReviewPrice}
                          onClick={reviewPriceHandler}
                          className={`w-100 btn text-center ${classes.view_transaction_approval}`}>
                          Done
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="unapproved_payment">
                  <div className="unapproved_payment_header mb-3 p-1 d-sm-flex justify-content-between">
                    <h2 className="w-100 mb-3">
                      {transaction?.lastname} {transaction?.firstname}
                    </h2>
                    <button
                      onClick={openNav}
                      type="button"
                      className="w-100 btn unapproved_payment_previous">
                      Previous Transactions
                    </button>
                  </div>
                  <div className="p-1 unapproved_payment_method">
                    <div className="mb-4">
                      {payments.map((value, index) => {
                        return (
                          <div
                            // key={`${Math.random().toString(36).slice(-6)}`}
                            key={`${index + 1}`}
                            className="row g-2 mb-3">
                            <div className="col-sm-3">
                              <div>
                                <p>Payment Method</p>
                                <select
                                  value={payments[index].payment_method}
                                  onChange={(event) => {
                                    setPayments((prev) => {
                                      // eslint-disable-next-line no-param-reassign
                                      prev[index] = {
                                        ...prev[index],
                                        payment_method: event.target.value
                                      };
                                      return [...prev];
                                    });
                                  }}
                                  className="form-select"
                                  aria-label="Default select example">
                                  <option>Select Payment Method</option>
                                  <option value="cash">Cash</option>
                                  <option value="transfer">Transfer</option>
                                  <option value="pos">P O S</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-sm-4">
                              <div>
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                <label htmlFor="amount" className="form-label m-0">
                                  Amount Payable
                                </label>
                                <input
                                  type="text"
                                  value={payments[index].amount}
                                  onChange={(event) => {
                                    setPayments((prev) => {
                                      // eslint-disable-next-line no-param-reassign
                                      prev[index] = {
                                        ...prev[index],
                                        amount: event.target.value
                                      };
                                      return [...prev];
                                    });
                                  }}
                                  className="form-control"
                                  id="amount"
                                  placeholder="N 0.00"
                                />
                              </div>
                            </div>
                            <div className="col-sm-4">
                              {payments[index].payment_method === 'transfer' && (
                                <div>
                                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                  <label htmlFor="bank_name" className="form-label m-0">
                                    Bank Name
                                  </label>
                                  <input
                                    type="text"
                                    value={payments[index].bank_name}
                                    onChange={(event) => {
                                      setPayments((prev) => {
                                        // eslint-disable-next-line no-param-reassign
                                        prev[index] = {
                                          ...prev[index],
                                          bank_name: event.target.value
                                        };
                                        return [...prev];
                                      });
                                    }}
                                    className="form-control"
                                    id="amount"
                                    placeholder="Bank Name"
                                  />
                                </div>
                              )}
                            </div>
                            <div className="col-1">
                              <div className="w-100 h-100 d-flex align-items-center">
                                {index === payments.length - 1 ? (
                                  <button
                                    className=""
                                    type="button"
                                    onClick={() => {
                                      setPayments((prev) => {
                                        prev.pop();
                                        return [...prev];
                                      });
                                    }}>
                                    &times;
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="w-100 d-flex justify-content-end">
                      <button
                        onClick={() => {
                          setPayments((prev) => {
                            return [
                              ...prev,
                              {
                                payment_method: '',
                                amount: '',
                                bank_name: ''
                              }
                            ];
                          });
                        }}
                        type="button"
                        className="btn unapproved_payment_button d-flex align-items-center justify-content-around">
                        <BsPlusLg className="me-2" /> Add To Payment
                      </button>
                    </div>
                  </div>
                  <div className="w-100 mt-5">
                    <button
                      type="button"
                      onClick={prepareApprovalHandler}
                      disabled={payments.length === 0}
                      className="btn unapproved_payment_approve w-100">
                      Approve Transaction
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div id="mySidenav" className="sidenav">
          <button type="button" className=" closebtn" onClick={closeNav}>
            &times;
          </button>
          <div className="sidenav_transactions">
            <h4 className="mb-4">Previous Transactions</h4>
            {prevLoading ? (
              <div className="d-flex justify-content-center w-100">
                <Spinner className="spinner" animation="border" variant="dark" />
              </div>
            ) : (
              <div>
                <div className="sidenav_transactions_tab p-2 mb-3">
                  <h5 className="mb-3">This Month</h5>
                  <div className="d-flex justify-content-between">
                    <div className="sidenav_transactions_tab_cell">
                      <p>{prevTransactions?.thisMonth_gas} KG</p>
                      <span>Gas Quantity Purchased</span>
                    </div>
                    <div className="sidenav_transactions_tab_cell">
                      <p>
                        {prevTransactions?.currency}{' '}
                        {nFormatter(Number(prevTransactions?.thisMonth_amount), 1)}
                      </p>
                      <span>Amount</span>
                    </div>
                  </div>
                </div>
                <div className="sidenav_transactions_tab p-2 mb-3">
                  <h5 className="mb-3">Last Month</h5>
                  <div className="d-flex justify-content-between">
                    <div className="sidenav_transactions_tab_cell">
                      <p>{prevTransactions?.lastMonth_gas} KG</p>
                      <span>Gas Quantity Purchased</span>
                    </div>
                    <div className="sidenav_transactions_tab_cell">
                      <p>
                        {prevTransactions?.currency}{' '}
                        {nFormatter(Number(prevTransactions?.lastMonth_amount), 1)}
                      </p>
                      <span>Amount</span>
                    </div>
                  </div>
                </div>
                <div className="sidenav_transactions_tab p-2 mb-4">
                  <h5 className="mb-3">3 Month Ago</h5>
                  <div className="d-flex justify-content-between">
                    <div className="sidenav_transactions_tab_cell">
                      <p>{prevTransactions?.threeMonth_gas} KG</p>
                      <span>Gas Quantity Purchased</span>
                    </div>
                    <div className="sidenav_transactions_tab_cell">
                      <p>
                        {prevTransactions?.currency}{' '}
                        {nFormatter(Number(prevTransactions?.threeMonth_amount), 1)}
                      </p>
                      <span>Amount</span>
                    </div>
                  </div>
                </div>
                <h5>Outstanding Debt</h5>
                <div className="sidenav_transactions_debt">
                  {prevTransactions?.currency}{' '}
                  {nFormatter(Number(Math.abs(prevTransactions?.totalDebt)), 1)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="unapproved_view_modals">
        <PromptModal
          title="Approve Transaction"
          message="Are you sure you want to approve this transaction"
          showPrompt={showApproveTransaction}
          loading={approveTransactionLoading}
          handlePromptClose={handleApproveTransactionClose}
          promptActionHandler={approveTransactionHandler}
        />
        <SuccessModal
          message="You've successfully approved this transaction"
          showSuccess={showSuccess}
          setShowSuccess={setShowSuccess}
          redirect="/unapproved"
        />
      </div>
      {printData && <Invoice printData={printData} />}
    </>
  );
};

export default UnApprovedTransactionView;
